import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  PROJECT_FINAL_PAYMENT_LABELS,
  projectFinalPayment,
  type Project,
  type ProjectBillingMode,
  type ProjectFinalPayment,
} from '@t3/shared';
import { FolderKanban } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { FormField } from '../components/FormField.tsx';
import { Select } from '../components/Select.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import { ViewsSidebar, type ViewDefinition } from '../components/ViewsSidebar.tsx';

interface OrgLite {
  id: string;
  name: string;
}

const VIEWS: ViewDefinition[] = [
  { id: 'my-open', label: 'My open projects', search: 'assignedAgentId=me&status=active' },
  { id: 'all-active', label: 'All active projects', search: 'status=active' },
  { id: 'at-risk', label: 'At-risk projects', search: 'atRisk=true' },
  { id: 'with-due', label: 'Upcoming due dates', search: 'hasDueDate=true&status=active&sort=due' },
  { id: 'with-cutover', label: 'Upcoming cutovers', search: 'hasCutoverDate=true&status=active&sort=cutover' },
  { id: 'final-payment', label: 'Collect final payment', search: 'finalPayment=to_collect' },
  { id: 'all', label: 'All projects', search: '' },
];

type ProjectStatus = 'active' | 'completed' | 'cancelled';

export function ProjectsListPage(): React.ReactElement {
  useDocumentTitle('Projects');
  const { agent } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showNewForm, setShowNewForm] = useState(false);
  const [orgs, setOrgs] = useState<OrgLite[] | null>(null);
  const [newOrgId, setNewOrgId] = useState('');
  const [newName, setNewName] = useState('');
  const [newHours, setNewHours] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const status = (searchParams.get('status') as ProjectStatus | null) ?? '';
  const assignedAgentParam = searchParams.get('assignedAgentId');
  const atRiskParam = searchParams.get('atRisk');
  const hasDueDateParam = searchParams.get('hasDueDate');
  const hasCutoverDateParam = searchParams.get('hasCutoverDate');
  const billingMode = (searchParams.get('billingMode') as ProjectBillingMode | null) ?? '';
  const finalPayment = (searchParams.get('finalPayment') as ProjectFinalPayment | null) ?? '';
  const sortParam = searchParams.get('sort'); // 'due' | 'cutover' | null

  const apiQuery = useMemo(() => {
    if (!agent) return null;
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (assignedAgentParam) {
      p.set('assignedAgentId', assignedAgentParam === 'me' ? agent.id : assignedAgentParam);
    }
    if (atRiskParam) p.set('atRisk', atRiskParam);
    if (hasDueDateParam) p.set('hasDueDate', hasDueDateParam);
    if (hasCutoverDateParam) p.set('hasCutoverDate', hasCutoverDateParam);
    if (billingMode) p.set('billingMode', billingMode);
    if (finalPayment) p.set('finalPayment', finalPayment);
    return p.toString();
  }, [agent, status, assignedAgentParam, atRiskParam, hasDueDateParam, hasCutoverDateParam, billingMode, finalPayment]);

  useEffect(() => {
    if (apiQuery === null) return;
    let cancelled = false;
    apiFetch<{ projects: Project[] }>(`/api/projects${apiQuery ? `?${apiQuery}` : ''}`)
      .then((r) => {
        if (!cancelled) {
          let list = r.projects;
          if (sortParam === 'due') {
            list = [...list].sort((a, b) => dateCompare(a.dueDate, b.dueDate));
          } else if (sortParam === 'cutover') {
            list = [...list].sort((a, b) => dateCompare(a.cutoverDate, b.cutoverDate));
          }
          setProjects(list);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load projects.');
      });
    return () => {
      cancelled = true;
    };
  }, [apiQuery, sortParam]);

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  }

  useEffect(() => {
    if (!showNewForm || orgs !== null) return;
    let cancelled = false;
    apiFetch<{ organizations: OrgLite[] }>('/api/organizations')
      .then((r) => {
        if (!cancelled) {
          const sorted = [...r.organizations].sort((a, b) => a.name.localeCompare(b.name));
          setOrgs(sorted);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setCreateError(err instanceof ApiError ? err.message : 'Failed to load organizations.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showNewForm, orgs]);

  function resetNewForm() {
    setNewOrgId('');
    setNewName('');
    setNewHours('');
    setCreateError(null);
  }

  async function createHoursProject() {
    setCreateError(null);
    const hours = Number(newHours);
    if (!newOrgId) {
      setCreateError('Choose an organization.');
      return;
    }
    if (!newName.trim()) {
      setCreateError('Project name is required.');
      return;
    }
    if (!Number.isFinite(hours) || hours <= 0) {
      setCreateError('Budgeted hours must be a positive number.');
      return;
    }
    setCreating(true);
    try {
      const r = await apiFetch<{ project: Project }>('/api/projects', {
        method: 'POST',
        json: {
          organizationId: newOrgId,
          name: newName.trim(),
          budgetedHours: hours,
          billingMode: 'block_hours',
        },
      });
      navigate(`/projects/${r.project.id}`);
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Failed to create project.');
    } finally {
      setCreating(false);
    }
  }

  const today = new Date(new Date().toDateString());
  const showDateColumn = sortParam === 'due' || sortParam === 'cutover' || atRiskParam === 'true';

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <ViewsSidebar title="Project views" views={VIEWS} basePath="/projects" />

      <div className="min-w-0 flex-1 space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[var(--color-header)]">Projects</h1>
            <p className="mt-1 text-[var(--color-muted)]">
              From accepted quotes (flat fee), promoted escalations (block hours), or created manually.
              Tracks budgeted vs. consumed.
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/projects/calendar" className="text-[var(--color-t3-blue)] hover:underline">
              Calendar
            </Link>
            <Link to="/projects/gantt" className="text-[var(--color-t3-blue)] hover:underline">
              Gantt
            </Link>
            <Button
              variant="secondary"
              onClick={() => {
                if (showNewForm) {
                  setShowNewForm(false);
                  resetNewForm();
                } else {
                  setShowNewForm(true);
                }
              }}
            >
              {showNewForm ? 'Cancel' : '+ New hours-based project'}
            </Button>
          </div>
        </header>

        {showNewForm && (
          <Card className="border-[var(--color-t3-blue)]/30 bg-blue-50/40">
            <h2 className="text-base font-semibold text-[var(--color-header)]">
              New hours-based project
            </h2>
            <p className="mt-1 text-sm text-[var(--color-muted)]">
              Creates a block-hour project with no source ticket. Use this when you need to track
              project time against a budget without going through a quote or escalation.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_140px]">
              <Select
                label="Organization"
                value={newOrgId}
                onChange={(e) => setNewOrgId(e.target.value)}
              >
                <option value="">{orgs === null ? 'Loading…' : 'Select organization'}</option>
                {(orgs ?? []).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
              <FormField
                label="Project name"
                name="new-project-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Q3 Network refresh"
              />
              <FormField
                label="Budgeted hours"
                name="new-project-hours"
                type="number"
                min="0.25"
                step="0.25"
                value={newHours}
                onChange={(e) => setNewHours(e.target.value)}
                placeholder="e.g. 20"
              />
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button onClick={() => void createHoursProject()} disabled={creating}>
                {creating ? 'Creating…' : 'Create project'}
              </Button>
              {createError && (
                <p className="text-sm text-[var(--color-danger)]">{createError}</p>
              )}
            </div>
          </Card>
        )}

        {atRiskParam === 'true' && (
          <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
            <p className="text-sm text-[var(--color-danger)]">
              Showing active projects that are <strong>over 80% budget</strong>, past their due date,
              or past their cutover date.
            </p>
          </Card>
        )}

        {/* On every view: a view sets these, and they narrow it further. */}
        <Card>
          <div className="grid gap-4 sm:grid-cols-3">
            <Select label="Status" value={status} onChange={(e) => updateFilter('status', e.target.value)}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
            <Select
              label="Type"
              value={billingMode}
              onChange={(e) => {
                const next = new URLSearchParams(searchParams);
                if (e.target.value) next.set('billingMode', e.target.value);
                else next.delete('billingMode');
                // Final payment is flat fee only.
                if (e.target.value === 'block_hours') next.delete('finalPayment');
                setSearchParams(next);
              }}
            >
              <option value="">All types</option>
              <option value="flat_fee">Flat rate</option>
              <option value="block_hours">Block hours</option>
            </Select>
            <Select
              label="Final payment"
              value={finalPayment}
              onChange={(e) => updateFilter('finalPayment', e.target.value)}
              disabled={billingMode === 'block_hours'}
            >
              <option value="">Any</option>
              {(Object.keys(PROJECT_FINAL_PAYMENT_LABELS) as ProjectFinalPayment[]).map((k) => (
                <option key={k} value={k}>
                  {PROJECT_FINAL_PAYMENT_LABELS[k]}
                </option>
              ))}
            </Select>
          </div>
        </Card>

        {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

        {projects === null ? (
          <p className="text-[var(--color-muted)]">Loading…</p>
        ) : projects.length === 0 ? (
          <EmptyState
            icon={<FolderKanban className="h-8 w-8" />}
            title="No projects match this view"
            body="Projects appear when an accepted quote is converted, an escalation is promoted, or you create one manually with the button above. Try a different view from the sidebar."
          />
        ) : (
          <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Org</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Type</th>
                  {showDateColumn && <th className="px-4 py-3">Due</th>}
                  {showDateColumn && <th className="px-4 py-3">Cutover</th>}
                  <th className="px-4 py-3">Budget</th>
                  <th className="px-4 py-3">Used</th>
                  <th className="px-4 py-3">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => {
                  const usedH = p.consumedMinutes / 60;
                  const remaining = p.budgetedHours - usedH;
                  const pct = p.budgetedHours > 0 ? Math.min(100, (usedH / p.budgetedHours) * 100) : 0;
                  const overBudget = remaining < 0;
                  const dueOver = p.dueDate !== null && p.status === 'active' && new Date(p.dueDate) < today;
                  const cutoverOver =
                    p.cutoverDate !== null && p.status === 'active' && new Date(p.cutoverDate) < today;
                  return (
                    <tr key={p.id} className="border-t border-[var(--color-border)]">
                      <td className="px-4 py-3">
                        <Link to={`/projects/${p.id}`} className="font-medium text-[var(--color-t3-blue)] hover:underline">
                          {p.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">{p.organizationName}</td>
                      <td className="px-4 py-3 capitalize">{p.status}</td>
                      <td className="px-4 py-3">
                        {p.billingMode === 'block_hours' ? 'Block hours' : 'Flat rate'}
                        {projectFinalPayment(p) === 'to_collect' && (
                          <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                            Collect final payment
                          </span>
                        )}
                        {projectFinalPayment(p) === 'collected' && (
                          <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                            Paid
                          </span>
                        )}
                      </td>
                      {showDateColumn && (
                        <td className={`px-4 py-3 ${dueOver ? 'font-semibold text-[var(--color-danger)]' : ''}`}>
                          {p.dueDate ?? '—'}
                        </td>
                      )}
                      {showDateColumn && (
                        <td className={`px-4 py-3 ${cutoverOver ? 'font-semibold text-[var(--color-danger)]' : ''}`}>
                          {p.cutoverDate ?? '—'}
                        </td>
                      )}
                      <td className="px-4 py-3">{p.budgetedHours.toFixed(2)}h</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span>{usedH.toFixed(2)}h</span>
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-[var(--color-border)]">
                            <div
                              className={`h-full ${pct >= 80 ? 'bg-[var(--color-danger)]' : 'bg-[var(--color-t3-blue)]'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className={`px-4 py-3 font-medium ${overBudget ? 'text-[var(--color-danger)]' : ''}`}>
                        {remaining.toFixed(2)}h
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function dateCompare(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a < b ? -1 : a > b ? 1 : 0;
}
